import _ from 'underscore';
import { useCallback, useMemo, useReducer } from 'react';

import { ActionTypes, searchReducer, initialState } from './search-reducer';
import { useLocationsFromMtApi } from './use-locations-from-mt-api';
import { useLocationsFromBingMapsApi } from './use-locations-from-bing-maps-api';
import { convertBingMapsResponseToOdata } from './convert-bing-maps-response-to-odata';
import { sanitizeQuery } from '../util/sanitize-query';

function isExpectedError(error) {
  if (error) {
    if (error.readyState === 0 && error.status === 0) {
      return true;
    }
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      return true;
    }
  }
  return false;
}

function errorToString(err) {
  return `${err instanceof Error ? err : JSON.stringify(err)}`;
}

/**
 * Hook that provides functions for searching MT Locations API and Bing Maps API.
 * @param {string} campaignApiServiceRoot - Root URL path to Campaign API.
 * @param {string} token - CCMTSmallToken, used to authenticate against MT.
 * @param {string} bingMapsApiKey - API key used for Bing Maps API requests. Can be obtained from
 * https://www.bingmapsportal.com/
 * @param {Object} [options] - Optional overrides for searching locations.
 * @param {string} userRegion - user region, userd to identify market.
 * @param {string} locale - locale, userd to identify language.
 * @param {Function} [options.getLocations] - Override the default odata call to get locations.
 * @param {Function} [options.onError] - Handles errors.
 * @param {Object} [options.instrumentationService] - Provides logging methods.
 * @returns {Array} - Returns the current search state and the search function.
 */
export function useBingMapsSearch(
  campaignApiServiceRoot,
  token,
  bingMapsApiKey,
  options,
  userRegion,
  locale
) {
  const [state, dispatch] = useReducer(searchReducer, initialState);

  const getLocationsFromBingMapsApi = useLocationsFromBingMapsApi(bingMapsApiKey, userRegion, locale);

  const getLocationsFromMtApi = useLocationsFromMtApi(campaignApiServiceRoot, token);
  const getLocations = useMemo(() => {
    if (options && _.isFunction(options.getLocations)) {
      return options.getLocations;
    }
    return getLocationsFromMtApi;
  }, [options, getLocationsFromMtApi]);

  const instrumentationService = useMemo(() => {
    if (options
      && _.isObject(options.instrumentationService)
      && _.isFunction(options.instrumentationService.logTrace)
      && _.isFunction(options.instrumentationService.logError)) {
      return options.instrumentationService;
    }
    return { logError: _.noop, logTrace: _.noop };
  }, [options]);

  const search = useCallback((query) => {
    if (!_.isString(query) || _.isEmpty(query)) {
      throw new Error('query must be a non-empty string');
    }
    const sanitizedQuery = sanitizeQuery(query);

    dispatch({
      type: ActionTypes.start,
      query: sanitizedQuery,
    });

    // search MT API first
    // NOTE: if getLocations Promise is rejected, we will NOT fallback to Bing Maps
    // This preserves the legacy behavior in ./feature/location-targets/js/view/locationSearch.es

    return getLocations(sanitizedQuery).then((response) => {
      const mtResponse = response;
      if (options && _.isFunction(options.filterSearchResults)) {
        mtResponse.value = options.filterSearchResults(mtResponse.value);
      }
      // return if MT response is valid and contains data
      if (mtResponse && (!_.isEmpty(mtResponse.value) || (options && options.skipBingMapAPISearch))) {
        instrumentationService.logTrace(`raw mt response:${JSON.stringify(mtResponse)}`);
        instrumentationService.logTrace(`getLocationsFromMtApi: Got ${_.size(mtResponse.value)} result(s).`);
        dispatch({
          type: ActionTypes.complete,
          data: mtResponse.value,
          query: sanitizedQuery,
        });
        return mtResponse;
      }

      // if no results from MT API, fallback to Bing Maps
      instrumentationService.logTrace('getLocationsFromMtApi: No results, falling back to Bing Maps API search.');
      return getLocationsFromBingMapsApi(sanitizedQuery).then((bingMapsResponse) => {
        let hasFilteredOutResults = false;
        instrumentationService.logTrace(`raw bing maps response:${JSON.stringify(bingMapsResponse)}`);
        // convert the bing maps format into odata format
        const convertedResponse = convertBingMapsResponseToOdata(bingMapsResponse);
        if (options && _.isFunction(options.filterSearchResults)) {
          const filteredResults = options.filterSearchResults(convertedResponse.value);
          if (filteredResults.length !== convertedResponse.value.length) {
            hasFilteredOutResults = true;
          }
          convertedResponse.value = filteredResults;
        }

        instrumentationService.logTrace(`getLocationsFromBingMapsApi: Got ${_.size(convertedResponse.value)} result(s).`);
        dispatch({
          type: ActionTypes.complete,
          data: convertedResponse.value,
          query: sanitizedQuery,
          hasFilteredOutResults,
        });

        return convertedResponse;
      });
    }).catch((error) => {
      const message = `useBingMapsSearch: error has occurred during search - ${errorToString(error)} (query: "${query}")`;
      if (isExpectedError(error)) {
        instrumentationService.logTrace(message);
      } else {
        instrumentationService.logError(message);
        if (options && _.isFunction(options.onError)) {
          options.onError(error);
        }
      }
      dispatch({
        type: ActionTypes.fail,
        error,
        query: sanitizedQuery,
      });

      throw error;
    });
  }, [getLocations, getLocationsFromBingMapsApi, options, instrumentationService]);

  return [state, { search }];
}
